import { observer } from 'mobx-react-lite';
import { PropsWithChildren, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { BasePage, BasePageProps, PageType } from 'src/components/BasePage';
import { Button } from 'src/components/Button/Button';
import { Form } from 'src/components/Form/Form';
import { ConstraintViolationError } from 'src/utils/ErrorDataHelper';
import { FormHelper } from 'src/utils/FormHelper';
import { BasicStore } from 'src/utils/mobx/BasicStore/BasicStore';
import styled from 'styled-components';

type CreateFormPageProps = PropsWithChildren<
    Pick<
        BasePageProps,
        | 'pageName'
        | 'isLoading'
        | 'showOnlyLoading'
        | 'title'
        | 'headerTopPanel'
    > & {
        store: BasicStore<any>;
        saveText?: ReactNode;
        basePageProps?: Partial<BasePageProps>;
        onCancel?: () => void;
        handleFinish?: (values: any) => Promise<void>;
        initialValues?: any;
    }
>;

export const CreateFormPage = observer(
    ({
        pageName,
        store,
        children,
        saveText,
        title,
        isLoading,
        showOnlyLoading,
        headerTopPanel,
        basePageProps,
        onCancel,
        handleFinish,
        initialValues,
    }: CreateFormPageProps) => {
        const { t } = useTranslation();

        const fields = FormHelper.getFieldDataItems(
            store.createItemLoader.errorData as ConstraintViolationError,
        );

        return (
            <BasePage
                showOnlyLoading={showOnlyLoading}
                pageName={pageName}
                type={PageType.BASE}
                isLoading={store.createItemLoader.isLoading || isLoading}
                title={title}
                headerTopPanel={headerTopPanel}
                {...basePageProps}
            >
                <Form
                    scrollToFirstError={{ block: 'center', behavior: 'smooth' }}
                    onFinish={handleFinish}
                    fields={fields}
                    initialValues={initialValues}
                >
                    {children}
                    <ButtonsContainer>
                        <Button onClick={onCancel}>
                            {t('App.Components.CreateFormPage.Cancel')}
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={store.createItemLoader.isLoading}
                        >
                            {saveText ||
                                t('App.Components.CreateFormPage.Save')}
                        </Button>
                    </ButtonsContainer>
                </Form>
            </BasePage>
        );
    },
);

const ButtonsContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-top: 40px;

    & > button:not(:last-child) {
        margin-right: 24px;
    }
`;
